<!--
    A bespoke inline hour/minute/AM-PM picker for the draft-schedule panel.

    NuxtUI (unlike PrimeVue's Calendar) doesn't ship a time-of-day component,
    so this reproduces the previous PrimeVue-based widget's look and
    increment/decrement interaction directly.
-->
<template>
    <div class="inline-flex w-full justify-center items-center gap-1">
        <div class="flex flex-col items-center gap-1">
            <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-7 border border-taupe-200 rounded-md bg-white text-taupe-600 cursor-pointer hover:bg-taupe-50 hover:border-taupe-300 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-white disabled:hover:border-taupe-200"
                :disabled="!canIncrementHour"
                @click="incrementHour"
            >
                <UIcon name="i-lucide-chevron-up" />
            </button>
            <span
                class="text-xl font-medium text-taupe-900 min-w-[1.75rem] text-center"
                >{{ String(hour12).padStart(2, '0') }}</span
            >
            <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-7 border border-taupe-200 rounded-md bg-white text-taupe-600 cursor-pointer hover:bg-taupe-50 hover:border-taupe-300 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-white disabled:hover:border-taupe-200"
                :disabled="!canDecrementHour"
                @click="decrementHour"
            >
                <UIcon name="i-lucide-chevron-down" />
            </button>
        </div>

        <div class="flex items-center px-0.5">
            <span class="text-xl font-medium text-taupe-400">:</span>
        </div>

        <div class="flex flex-col items-center gap-1">
            <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-7 border border-taupe-200 rounded-md bg-white text-taupe-600 cursor-pointer hover:bg-taupe-50 hover:border-taupe-300 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-white disabled:hover:border-taupe-200"
                :disabled="!canIncrementMinute"
                @click="incrementMinute"
            >
                <UIcon name="i-lucide-chevron-up" />
            </button>
            <span
                class="text-xl font-medium text-taupe-900 min-w-[1.75rem] text-center"
                >{{ String(minute).padStart(2, '0') }}</span
            >
            <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-7 border border-taupe-200 rounded-md bg-white text-taupe-600 cursor-pointer hover:bg-taupe-50 hover:border-taupe-300 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-white disabled:hover:border-taupe-200"
                :disabled="!canDecrementMinute"
                @click="decrementMinute"
            >
                <UIcon name="i-lucide-chevron-down" />
            </button>
        </div>

        <div class="flex flex-col items-center gap-1">
            <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-7 border border-taupe-200 rounded-md bg-white text-taupe-600 cursor-pointer hover:bg-taupe-50 hover:border-taupe-300 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-white disabled:hover:border-taupe-200"
                :disabled="!canToggleAmPm"
                @click="toggleAmPm"
            >
                <UIcon name="i-lucide-chevron-up" />
            </button>
            <span
                class="text-lg font-medium text-taupe-900 min-w-[2rem] text-center"
                >{{ ampm }}</span
            >
            <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-7 border border-taupe-200 rounded-md bg-white text-taupe-600 cursor-pointer hover:bg-taupe-50 hover:border-taupe-300 disabled:opacity-40 disabled:cursor-default disabled:hover:bg-white disabled:hover:border-taupe-200"
                :disabled="!canToggleAmPm"
                @click="toggleAmPm"
            >
                <UIcon name="i-lucide-chevron-down" />
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    // A Date (or null, before the parent has a value to show).
    modelValue: {
        default: null,
        required: false,
        type: null,
    },
    // Only times at or after this Date (compared minute-by-minute, same day)
    // are selectable — used to keep "today" schedules from being set in the
    // past. `null` means no restriction.
    minDate: {
        default: null,
        required: false,
        type: null,
    },
})

const emit = defineEmits(['update:modelValue'])

const value = computed(() => props.modelValue ?? new Date())

const hour24 = computed(() => value.value.getHours())
const minute = computed(() => value.value.getMinutes())
const ampm = computed(() => (hour24.value >= 12 ? 'PM' : 'AM'))
const hour12 = computed(() => {
    const remainder = hour24.value % 12
    return remainder === 0 ? 12 : remainder
})

function isSameDay(a, b) {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    )
}

function minutesOfDayFor(hour24Value, minuteValue) {
    return hour24Value * 60 + minuteValue
}

// The floor only restricts times on the same calendar day as `minDate` — a
// floor from a different day doesn't say anything about today's picks.
const minMinutesOfDay = computed(() => {
    if (!props.minDate || !isSameDay(props.minDate, value.value)) return null

    return minutesOfDayFor(props.minDate.getHours(), props.minDate.getMinutes())
})

// Each `can*` check compares the *actual* minutes-of-day the action would
// produce (not an offset like "+/-60") so a wrap-around — e.g. toggling
// PM back to AM, which moves earlier in the day despite "adding" 12 hours
// mod 24 — is validated against what really happens, not just one direction.
const canDecrementHour = computed(() => {
    if (minMinutesOfDay.value === null) return true
    return (
        minutesOfDayFor((hour24.value + 23) % 24, minute.value) >=
        minMinutesOfDay.value
    )
})
const canIncrementHour = computed(() => true)
const canDecrementMinute = computed(() => {
    if (minMinutesOfDay.value === null) return true
    return (
        minutesOfDayFor(hour24.value, (minute.value + 59) % 60) >=
        minMinutesOfDay.value
    )
})
const canIncrementMinute = computed(() => true)
const canToggleAmPm = computed(() => {
    if (minMinutesOfDay.value === null) return true
    return (
        minutesOfDayFor((hour24.value + 12) % 24, minute.value) >=
        minMinutesOfDay.value
    )
})

function emitHoursMinutes(nextHour24, nextMinute) {
    const next = new Date(value.value)
    next.setHours(nextHour24, nextMinute, 0, 0)
    emit('update:modelValue', next)
}

function incrementHour() {
    emitHoursMinutes((hour24.value + 1) % 24, minute.value)
}

function decrementHour() {
    if (!canDecrementHour.value) return
    emitHoursMinutes((hour24.value + 23) % 24, minute.value)
}

function incrementMinute() {
    emitHoursMinutes(hour24.value, (minute.value + 1) % 60)
}

function decrementMinute() {
    if (!canDecrementMinute.value) return
    emitHoursMinutes(hour24.value, (minute.value + 59) % 60)
}

function toggleAmPm() {
    if (!canToggleAmPm.value) return
    emitHoursMinutes((hour24.value + 12) % 24, minute.value)
}
</script>
